import { Link } from 'react-router-dom';
import { siteInfo } from '@/mocks/siteInfo';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, getLatestArticles } from '@/mocks/articles';
import { usePageSEO } from '@/hooks/usePageSEO';
import { getOrganizationSchema, getWebSiteSchema, getWebPageSchema, siteDomain } from '@/utils/schema';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const featuredArticles = getFeaturedArticles(6);
const latestArticles = getLatestArticles(6);

export default function HomePage() {
  usePageSEO({
    title: 'kaiyuntiyu官方落地页-专业体育平台 | 体育资讯·赛事数据·直播服务',
    description: siteInfo.description,
    keywords: 'kaiyuntiyu,kaiyuntiyu官网,kaiyuntiyu平台,kaiyuntiyu体育直播,kaiyuntiyu赛事数据,kaiyuntiyu注册登录,kaiyuntiyu最新资讯',
    canonicalUrl: siteDomain,
    schema: [getOrganizationSchema(), getWebSiteSchema(), getWebPageSchema('kaiyuntiyu官方落地页-专业体育平台', siteInfo.description, siteDomain)],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dynamic%20sports%20collage%20background%20with%20abstract%20geometric%20patterns%20in%20warm%20crimson%20and%20gold%20tones%2C%20athletic%20silhouettes%20in%20motion%2C%20stadium%20lights%20atmosphere%2C%20energy%20waves%2C%20modern%20sports%20aesthetic%2C%20clean%20gradient%20overlay%2C%20no%20text%2C%20abstract%20style&width=1600&height=900&seq=hero-kaiyuntiyu-2026&orientation=landscape"
              alt="kaiyuntiyu 专业体育平台"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
          </div>
          <div className="relative z-10 w-full text-center px-4 md:px-6 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6">
              <i className="ri-fire-line text-accent-400 text-sm"></i>
              <span className="text-xs md:text-sm text-background-100 font-medium">
                {siteInfo.stats.registeredUsers} 注册用户信赖之选
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading leading-tight mb-4">
              汇聚热爱，点燃激情
            </h1>
            <p className="text-sm md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-all duration-200 whitespace-nowrap no-underline text-sm"
              >
                探索赛事资讯
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/10 hover:bg-background-50/20 backdrop-blur-sm text-background-50 font-semibold rounded-lg border border-background-50/25 transition-all duration-200 whitespace-nowrap no-underline text-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: siteInfo.stats.registeredUsers, label: '注册用户', icon: 'ri-user-star-line' },
              { value: siteInfo.stats.dailyActiveUsers, label: '日活跃用户', icon: 'ri-group-line' },
              { value: siteInfo.stats.articlesPublished, label: '资讯文章', icon: 'ri-article-line' },
              { value: siteInfo.stats.coverage, label: '赛事覆盖', icon: 'ri-global-line' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-100/70 rounded-lg">
                  <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Category Quick Links */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                探索体育分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                五大分类覆盖全球主流体育项目，满足每一位体育爱好者的需求
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 rounded-xl p-5 text-center border border-background-200/70 hover:border-primary-300 hover:shadow-sm transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center bg-primary-100/70 rounded-xl group-hover:bg-primary-200/80 transition-colors">
                    <i className={`${cat.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1 font-heading">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500">
                    {cat.articleCount} 篇文章
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  热门资讯
                </h2>
                <p className="text-sm text-foreground-600">最受关注的体育资讯，热度排行TOP6</p>
              </div>
              <Link
                to="/category"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap no-underline"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => {
                const category = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          {category?.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i> {article.author}
                        </span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-3">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  最新资讯
                </h2>
                <p className="text-sm text-foreground-600">24小时不间断更新，第一时间传递赛场动态</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {latestArticles.map((article, index) => {
                const category = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className={`group flex flex-col sm:flex-row gap-4 p-4 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer ${
                      index === 0 ? 'lg:col-span-2' : ''
                    }`}
                  >
                    <div className={`relative overflow-hidden rounded-lg flex-shrink-0 ${
                      index === 0
                        ? 'w-full sm:w-64 h-40 sm:h-36'
                        : 'w-full sm:w-48 h-40 sm:h-32'
                    }`}>
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                          {category?.name}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {(article.views / 1000).toFixed(1)}k 阅读
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20crimson%20and%20gold%20flowing%20energy%20waves%2C%20modern%20sports%20atmosphere%2C%20soft%20light%20beams%2C%20clean%20minimal%20style%2C%20no%20text%2C%20smooth%20gradient%20transitions&width=1600&height=600&seq=cta-kaiyuntiyu-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/90"></div>
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading mb-4">
              准备好探索精彩的体育世界了吗？
            </h2>
            <p className="text-sm md:text-base text-background-200/90 mb-8 max-w-lg mx-auto">
              立即加入 {siteInfo.stats.registeredUsers} 体育爱好者社区，获取最新资讯和专业数据分析
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50 text-primary-600 font-semibold rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap no-underline text-sm"
              >
                立即注册
              </Link>
              <Link
                to="/category"
                className="px-8 py-3 bg-background-50/10 backdrop-blur-sm text-background-50 font-semibold rounded-lg border border-background-50/25 hover:bg-background-50/20 transition-all duration-200 whitespace-nowrap no-underline text-sm"
              >
                浏览资讯
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}